import { type ReactNode } from 'react'
import { cn } from '@/utils/cn'
import { AnimatedKpiValue, type KpiValueFormat } from '@/components/ui/AnimatedKpiValue'
import { formatCurrencyCompact } from '@/utils/format-currency'
import { KpiSparkline } from '@/components/ui/KpiSparkline'
import {
  ensureTrendSeries,
  trendChangePercent,
  trendIsPositive,
} from '@/utils/kpiTrend'

interface CardProps {
  title?: string
  description?: string
  children?: ReactNode
  actions?: ReactNode
  className?: string
  hoverable?: boolean
  padding?: 'none' | 'sm' | 'md' | 'lg'
}

const paddingClasses = {
  none: 'p-0',
  sm: 'p-3',
  md: 'p-5',
  lg: 'p-6',
}

export function Card({
  title,
  description,
  children,
  actions,
  className,
  hoverable = false,
  padding = 'md',
}: CardProps) {
  return (
    <div
      className={cn(
        'glass-card',
        paddingClasses[padding],
        hoverable && [
          'cursor-pointer',
          'hover:border-[var(--brand-primary)] hover:border-opacity-40',
        ],
        className
      )}
    >
      {(title || actions) && (
        <div className='flex items-start justify-between mb-4'>
          <div>
            {title && (
              <h3
                className='font-semibold text-[var(--font-color-h3)]'
                style={{ fontSize: 'var(--font-size-h3)' }}
              >
                {title}
              </h3>
            )}
            {description && (
              <p
                className='mt-0.5 text-[var(--font-color-muted)]'
                style={{ fontSize: 'var(--font-size-sm)' }}
              >
                {description}
              </p>
            )}
          </div>
          {actions && <div className='flex items-center gap-2'>{actions}</div>}
        </div>
      )}
      {children}
    </div>
  )
}

// Card de KPI para o Dashboard
interface KpiCardProps {
  label: string
  value: string
  numericValue?: number
  valueFormat?: KpiValueFormat
  change?: string
  changePositive?: boolean
  /** Série temporal real para o sparkline (ex.: receita mensal). */
  trend?: number[]
  trendLabel?: string
  icon?: ReactNode
  sparklineColor?: string
  sparklineFill?: string
  animationDelay?: number
  className?: string
  valuesVisible?: boolean
}

export function KpiCard({
  label,
  value,
  numericValue,
  valueFormat = 'text',
  change,
  changePositive,
  trend,
  trendLabel,
  icon,
  sparklineColor = 'var(--brand-primary)',
  sparklineFill = 'rgba(152, 125, 232, 0.14)',
  animationDelay = 0,
  className,
  valuesVisible = true,
}: KpiCardProps) {
  const series = valuesVisible && trend ? ensureTrendSeries(trend) : null
  const autoDelta = series ? trendChangePercent(series) : null
  const positive =
    changePositive ?? (autoDelta != null ? trendIsPositive(series!) : true)

  const deltaText = valuesVisible
    ? change ??
      (autoDelta != null && trendLabel
        ? `${Math.abs(autoDelta).toFixed(1)}% ${trendLabel}`
        : undefined)
    : undefined

  const useCompactCurrency =
    valuesVisible && valueFormat === 'currency' && (numericValue ?? 0) >= 100_000

  const displayValue = valuesVisible ? value : '••••'
  const animatedNumericValue = valuesVisible ? numericValue : undefined

  return (
    <div
      className={cn(
        'glass-card grid min-h-[128px] cursor-pointer grid-cols-[1fr_4.5rem] grid-rows-[auto_auto_auto] gap-x-2 gap-y-2 p-4',
        'hover:border-[var(--brand-primary)] hover:border-opacity-40 transition-all duration-150',
        className,
      )}
    >
      <p
        className='col-start-1 row-start-1 min-w-0 truncate text-[var(--font-color-label)] font-medium uppercase tracking-wider'
        style={{ fontSize: 'var(--font-size-label)' }}
      >
        {label}
      </p>

      {icon && (
        <div className='col-start-2 row-start-1 flex justify-end'>
          <div className='rounded-lg bg-[var(--brand-primary-muted)] p-1.5 text-[var(--brand-primary)]'>
            {icon}
          </div>
        </div>
      )}

      <div
        className='col-start-1 row-start-2 min-w-0 self-center'
        title={useCompactCurrency ? value : undefined}
      >
        <AnimatedKpiValue
          numericValue={animatedNumericValue}
          displayValue={useCompactCurrency && animatedNumericValue != null
            ? formatCurrencyCompact(animatedNumericValue)
            : displayValue}
          format={valueFormat}
          delay={animationDelay}
          compactCurrency={useCompactCurrency}
        />
      </div>

      {series && (
        <div
          className='col-start-2 row-start-2 flex h-10 w-full items-center justify-end self-center'
          aria-hidden
        >
          <KpiSparkline
            data={series}
            strokeColor={sparklineColor}
            fillColor={sparklineFill}
            delay={animationDelay}
            className='h-10 w-[4.5rem]'
          />
        </div>
      )}

      {deltaText && (
        <p
          className={cn(
            'col-span-2 row-start-3 min-w-0 truncate text-xs font-medium',
            positive ? 'text-[#27a644]' : 'text-[#e5484d]',
          )}
        >
          {positive ? '↑' : '↓'} {deltaText}
        </p>
      )}
    </div>
  )
}
